import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getFileContentUrl } from '@/services/docflowService';
import type { IOutputField } from '@/types/docflow';

// Color palette for field annotations (hue rotation)
const ANNOTATION_COLORS = [
  { bg: 'rgba(239, 68, 68, 0.18)', border: 'rgba(239, 68, 68, 0.7)', text: '#ef4444' },   // red
  { bg: 'rgba(59, 130, 246, 0.18)', border: 'rgba(59, 130, 246, 0.7)', text: '#3b82f6' },   // blue
  { bg: 'rgba(16, 185, 129, 0.18)', border: 'rgba(16, 185, 129, 0.7)', text: '#10b981' },   // emerald
  { bg: 'rgba(245, 158, 11, 0.18)', border: 'rgba(245, 158, 11, 0.7)', text: '#f59e0b' },   // amber
  { bg: 'rgba(139, 92, 246, 0.18)', border: 'rgba(139, 92, 246, 0.7)', text: '#8b5cf6' },   // violet
  { bg: 'rgba(236, 72, 153, 0.18)', border: 'rgba(236, 72, 153, 0.7)', text: '#ec4899' },   // pink
  { bg: 'rgba(6, 182, 212, 0.18)', border: 'rgba(6, 182, 212, 0.7)', text: '#06b6d4' },     // cyan
  { bg: 'rgba(249, 115, 22, 0.18)', border: 'rgba(249, 115, 22, 0.7)', text: '#f97316' },   // orange
];

interface FieldLocation {
  yMin: number;
  xMin: number;
  yMax: number;
  xMax: number;
}

function parseFieldLocation(raw: unknown): FieldLocation | null {
  if (!Array.isArray(raw) || raw.length !== 4) return null;
  const nums = raw.map(Number);
  if (nums.some(isNaN)) return null;
  return { yMin: nums[0], xMin: nums[1], yMax: nums[2], xMax: nums[3] };
}

interface DocumentAnnotationViewerProps {
  fileId: string;
  mimeType: string;
  fieldLocations: Record<string, unknown>;
  extractedData: Record<string, unknown>;
  fields?: IOutputField[];
}

const DocumentAnnotationViewer: React.FC<DocumentAnnotationViewerProps> = ({
  fileId,
  mimeType,
  fieldLocations,
  extractedData,
  fields,
}) => {
  const { t } = useTranslation('docflow');
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isImage = mimeType.startsWith('image/');

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // Build field label lookup from fields prop
  const fieldLabels: Record<string, string> = {};
  if (fields) {
    for (const f of fields) {
      fieldLabels[f.key] = f.label;
    }
  }

  // Get all annotation entries
  const annotations = Object.entries(fieldLocations)
    .map(([key, raw], index) => {
      const loc = parseFieldLocation(raw);
      if (!loc) return null;
      return { key, loc, color: ANNOTATION_COLORS[index % ANNOTATION_COLORS.length] };
    })
    .filter(Boolean) as { key: string; loc: FieldLocation; color: typeof ANNOTATION_COLORS[0] }[];

  if (!isImage) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400 italic py-2">
        {t('execution.pdfNoPreview')}
      </div>
    );
  }

  const token = localStorage.getItem('auth_token') || '';
  const imgSrc = `${getFileContentUrl(fileId)}${token ? `?token=${encodeURIComponent(token)}` : ''}`;

  return (
    <div className="mt-3">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        {t('execution.annotationsHint')}
      </div>

      <div ref={containerRef} className="relative inline-block max-w-full">
        <img
          src={imgSrc}
          alt="Source document"
          className="max-w-full max-h-[600px] rounded border border-gray-200 dark:border-gray-700"
          onLoad={handleImageLoad}
          crossOrigin="anonymous"
        />

        {imageLoaded && annotations.map(({ key, loc, color }) => {
          const top = (loc.yMin / 1000) * 100;
          const left = (loc.xMin / 1000) * 100;
          const width = ((loc.xMax - loc.xMin) / 1000) * 100;
          const height = ((loc.yMax - loc.yMin) / 1000) * 100;
          const isHovered = hoveredField === key;
          const label = fieldLabels[key] || key;
          const value = extractedData[key];

          return (
            <div
              key={key}
              className="absolute cursor-pointer transition-opacity"
              style={{
                top: `${top}%`,
                left: `${left}%`,
                width: `${width}%`,
                height: `${height}%`,
                backgroundColor: color.bg,
                border: `2px solid ${color.border}`,
                borderRadius: '2px',
                opacity: isHovered ? 1 : 0.7,
                zIndex: isHovered ? 20 : 10,
              }}
              onMouseEnter={() => setHoveredField(key)}
              onMouseLeave={() => setHoveredField(null)}
            >
              {/* Field label tag */}
              <div
                className="absolute -top-5 left-0 px-1 py-0.5 text-[10px] font-semibold rounded whitespace-nowrap"
                style={{
                  backgroundColor: color.border,
                  color: 'white',
                }}
              >
                {label}
              </div>

              {/* Tooltip on hover */}
              {isHovered && value != null && (
                <div
                  className="absolute top-full left-0 mt-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap z-30"
                >
                  <span className="font-semibold">{label}:</span> {String(value)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      {annotations.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {annotations.map(({ key, color }) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded cursor-pointer"
              style={{
                backgroundColor: color.bg,
                border: `1px solid ${color.border}`,
                color: color.text,
              }}
              onMouseEnter={() => setHoveredField(key)}
              onMouseLeave={() => setHoveredField(null)}
            >
              {fieldLabels[key] || key}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentAnnotationViewer;
